import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@/ui/layout';
import { CircularProgressBase } from '@/ui/circular-progress/circular-progress-base';
import { DeleteOutlineIcon, UndoIcon } from '@/ui/icons';
import { IconButton } from '@/ui/icon-button/icon-button';
import { useStoreLayerDeletionStartTime } from '@/core/stores/states/layer-state';
import { logger } from '@/core/utils/logger';
import { TIMEOUT } from '@/core/utils/constant';
import { getGVElementByFullId } from '@/core/utils/dom-helper';
import { useStoreGeoViewMapId } from '@/core/stores/geoview-store';
import { useLayerController } from '@/core/controllers/use-controllers';
import { getSxClasses } from './delete-undo-button-style';

/** Sx classes for this component. No theme dependency, so computed once at module scope. */
const sxClasses = getSxClasses();

/** Props for the UndoButtonWithProgress component. */
interface UndoButtonProps {
  /** Elapsed percentage of the layer deletion undo window. */
  progressValue: number;
  /** Callback invoked when the undo button is activated. */
  onUndo: (event: MouseEvent) => void;
  /** Ref used to focus the undo button after keyboard activation of delete. */
  iconRef: React.RefObject<HTMLButtonElement | null>;
}

/**
 * Creates the undo button with a progress indicator for the remaining undo window.
 *
 * @param props - Properties defined in UndoButtonProps interface
 * @returns The undo button and deletion progress indicator
 */
function UndoButtonWithProgress(props: UndoButtonProps): JSX.Element {
  // Log
  logger.logTraceRender('components/layers/delete-undo-button/UndoButtonWithProgress');

  const { t } = useTranslation<string>();

  const { progressValue, onUndo, iconRef } = props;

  return (
    <Box sx={sxClasses.undoButtonContainer}>
      <CircularProgressBase variant="determinate" size={40} value={progressValue} sx={sxClasses.progressNoTransition} />
      <Box sx={sxClasses.undoIconOverlay}>
        <IconButton iconRef={iconRef} aria-label={t('layers.undoLayer')} edge="end" size="small" onClick={onUndo}>
          <UndoIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

/** Props for the DeleteUndoButton component. */
interface DeleteUndoButtonProps {
  /** Path of the layer to delete or restore. */
  layerPath: string;
  /** Whether the layer permits removal. */
  layerRemovable: boolean;
  /** Optional map-scoped element ID to focus after deletion completes. */
  focusTargetIdAfterDelete?: string;
}

/**
 * Creates the layer delete button with a temporary undo action and keyboard focus restoration.
 *
 * @param props - Properties defined in DeleteUndoButtonProps interface
 * @returns The delete or undo button for the layer
 */
export function DeleteUndoButton(props: DeleteUndoButtonProps): JSX.Element {
  // Log
  logger.logTraceRender('components/layers/delete-undo-button/DeleteUndoButton');

  const { layerPath, layerRemovable, focusTargetIdAfterDelete } = props;

  const { t } = useTranslation<string>();

  // Store
  const mapId = useStoreGeoViewMapId();

  // Refs for buttons to manage focus
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const undoButtonRef = useRef<HTMLButtonElement>(null);

  // Store hooks
  const layerDeletionStartTime = useStoreLayerDeletionStartTime(layerPath);
  const layerController = useLayerController();

  // state
  const [progress, setProgress] = useState(0);

  /**
   * Performs the delete operation on the layer.
   *
   * @param wasKeyboardActivated - Whether deletion was activated via the keyboard
   */
  const performDelete = (wasKeyboardActivated: boolean): void => {
    // If was keyboard activated on clicking
    if (wasKeyboardActivated) {
      requestAnimationFrame(() => {
        // Focus on the undo button ref now
        undoButtonRef.current?.focus();
      });
    }

    // Delete the layer
    layerController
      .deleteLayerStartTimer(layerPath, TIMEOUT.deleteLayerUndoWindow)
      .then((deleted) => {
        // If deleted, set focus elsewhere
        if (deleted && focusTargetIdAfterDelete) {
          const targetId = focusTargetIdAfterDelete;
          requestAnimationFrame(() => {
            getGVElementByFullId(mapId, targetId)?.focus();
          });
        }
      })
      .catch((error: unknown) => {
        // Log error
        logger.logPromiseFailed('in deleteLayer in handleDeleteClick', error);
      });
  };

  /**
   * Performs the undo operation on the layer.
   *
   * @param wasKeyboardActivated - Whether undo was activated via the keyboard
   */
  const performUndo = (wasKeyboardActivated: boolean): void => {
    // Call the action
    layerController.deleteLayerAbort(layerPath);

    // If was keyboard activated on clicking
    if (wasKeyboardActivated) {
      // Focus on the delete button ref now
      requestAnimationFrame(() => deleteButtonRef.current?.focus());
    }
  };

  // #region Handlers

  /**
   * Handles when the user activates the delete button.
   */
  const handleDeleteClick = (event: MouseEvent): void => {
    // A click with detail 0 was synthesized from the keyboard (Enter/Space), so focus must be managed
    performDelete(event.detail === 0);
  };

  /**
   * Handles when the user activates the undo button.
   */
  const handleUndoClick = (event: MouseEvent): void => {
    // A click with detail 0 was synthesized from the keyboard (Enter/Space), so focus must be managed
    performUndo(event.detail === 0);
  };

  // #endregion Handlers

  /**
   * Animates deletion progress while the undo window is active and cancels the animation on cleanup.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('components/layers/delete-undo-button/DeleteUndoButton: DELETION PROGRESS ANIMATION', layerDeletionStartTime);

    // If no deletion happening
    if (!layerDeletionStartTime) return undefined;

    // Use requestAnimationFrame to update the progress percentage instead of relying on a value
    // constantly being updated coming from the store, for performance.
    // RAF syncs with the browser repaint cycle for smoother animation and auto-pauses when the tab is hidden.
    // For the RAF to work, the transition on the MuiCircularProgress-circle component must be set to none,
    // otherwise it will cause the progress circle to only update on weird force renders.
    let frameId: number;
    const animate = (): void => {
      const elapsed = Date.now() - layerDeletionStartTime;
      const pct = Math.min((elapsed / TIMEOUT.deleteLayerUndoWindow) * 100, 100);
      setProgress(pct);
      if (pct < 100) {
        frameId = requestAnimationFrame(animate);
      }
    };
    frameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [layerDeletionStartTime]);

  // Never hide the remove icon, so user can remove forever loading/processing layers.
  if (layerRemovable && !layerDeletionStartTime) {
    return (
      <IconButton iconRef={deleteButtonRef} onClick={handleDeleteClick} className="buttonOutline" aria-label={t('layers.deleteLayer')}>
        <DeleteOutlineIcon color="error" />
      </IconButton>
    );
  }
  if (!layerRemovable) {
    return (
      <IconButton
        iconRef={deleteButtonRef}
        aria-label={t('layers.deleteLayer')}
        onClick={handleDeleteClick}
        className="buttonOutline"
        disabled
      >
        <DeleteOutlineIcon color="disabled" />
      </IconButton>
    );
  }
  return <UndoButtonWithProgress iconRef={undoButtonRef} progressValue={progress} onUndo={handleUndoClick} />;
}
